import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Snackbar, SnackbarCloseReason, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useDebounce } from '../../../shared/hooks';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/ContaBancaria';
import { CaixaConstasService } from '../../../shared/services/api/Financeiro/Caixa';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';

export interface DialogTransferirRecebivelProps {
  open: boolean;
  idcontaRecebivel: string;
  nomeContaRecebivel?: string;
  onClose: (value?: string) => void;
}

type TContaOpcao = { idconta: string; label: string };

const formatMoeda = (valor: number) => formatarMoeda(Number(valor || 0));

// Liquidação de recebível: a operadora da maquininha (Rede, Cielo, Stone...)
// deposita o valor de um cartão na conta bancária, sempre descontando uma
// taxa — esse diálogo registra os dois números (o que foi cobrado no cartão
// e o que a operadora realmente pagou) e calcula a taxa automaticamente. Só
// o valor líquido é transferido pra conta de destino; a taxa fica lançada
// como uma saída extra dentro do próprio recebível (ver
// TransferenciaRecebivel.ts, backend).
export const DialogTransferirRecebivel: React.FC<DialogTransferirRecebivelProps> = ({
  open, idcontaRecebivel, nomeContaRecebivel, onClose,
}) => {
  const { debounce } = useDebounce();

  const [busca, setBusca] = useState('');
  const [opcoesConta, setOpcoesConta] = useState<TContaOpcao[]>([]);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [contaDestino, setContaDestino] = useState<TContaOpcao | null>(null);

  const [operadora, setOperadora] = useState('');
  const [data, setData] = useState(dayjs().format('YYYY-MM-DD'));
  const [valorBrutoTexto, setValorBrutoTexto] = useState('');
  const [valorLiquidoTexto, setValorLiquidoTexto] = useState('');

  const [transferindo, setTransferindo] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  useEffect(() => {
    if (!open) return;
    setBusca('');
    setContaDestino(null);
    setOperadora('');
    setData(dayjs().format('YYYY-MM-DD'));
    setValorBrutoTexto('');
    setValorLiquidoTexto('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setCarregandoContas(true);
    debounce(() => {
      ContaBancariaService.getAllList(busca, '', 'banco', 20).then((resultado) => {
        setCarregandoContas(false);
        if (resultado instanceof Error) { setOpcoesConta([]); return; }
        setOpcoesConta(resultado.data.map((conta) => ({ idconta: conta.idconta, label: conta.titular })));
      });
    });
  }, [open, busca, debounce]);

  const valorBruto = valorBrutoTexto ? Number(valorBrutoTexto) / 100 : 0;
  const valorLiquido = valorLiquidoTexto ? Number(valorLiquidoTexto) / 100 : 0;
  const taxa = useMemo(() => Math.max(0, Number((valorBruto - valorLiquido).toFixed(2))), [valorBruto, valorLiquido]);

  const exibir = (valor: number) => (valor === 0 ? '' : formatarMoeda(valor));
  const handleChangeValorBruto = (texto: string) => setValorBrutoTexto(texto.replace(/\D/g, ''));
  const handleChangeValorLiquido = (texto: string) => setValorLiquidoTexto(texto.replace(/\D/g, ''));

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    if (tms === 'success') onClose('true');
  };

  const confirmar = () => {
    if (!contaDestino) {
      setTms('warning');
      setMsg('Selecione a conta de destino');
      setOpenmsg(true);
      return;
    }
    if (valorBruto <= 0) {
      setTms('warning');
      setMsg('Informe o valor pago no cartão');
      setOpenmsg(true);
      return;
    }
    if (valorLiquido <= 0) {
      setTms('warning');
      setMsg('Informe o valor que a operadora depositou');
      setOpenmsg(true);
      return;
    }
    if (valorLiquido > valorBruto) {
      setTms('warning');
      setMsg('O valor depositado não pode ser maior que o valor pago no cartão');
      setOpenmsg(true);
      return;
    }

    setTransferindo(true);
    CaixaConstasService
      .TransferirRecebivel({
        idcontarecebivel: idcontaRecebivel,
        idcontadestino: contaDestino.idconta,
        valorbruto: valorBruto,
        valorliquido: valorLiquido,
        operadora,
        datalan: dayjs(data, 'YYYY-MM-DD').toDate(),
      })
      .then((resultado) => {
        setTransferindo(false);
        if (resultado instanceof Error) {
          setTms('error');
          setMsg(resultado.message);
          setOpenmsg(true);
          return;
        }
        setTms('success');
        setMsg('Transferência registrada com sucesso!');
        setOpenmsg(true);
      });
  };

  const handleCancel = () => onClose('false');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Transferir recebível{nomeContaRecebivel ? ` — ${nomeContaRecebivel}` : ''}</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={2500} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Conta de destino (banco)
        </Typography>
        <Autocomplete
          openText='Abrir'
          closeText='Fechar'
          noOptionsText='Nenhuma conta encontrada'
          loadingText='Carregando...'
          disablePortal
          options={opcoesConta}
          loading={carregandoContas}
          disabled={transferindo}
          value={contaDestino}
          onInputChange={(_, newValue) => setBusca(newValue)}
          onChange={(_, newValue) => setContaDestino(newValue)}
          popupIcon={carregandoContas ? <CircularProgress size={20} /> : undefined}
          renderInput={(params) => (
            <TextField {...params} size='small' label='Conta de destino' />
          )}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Operadora da maquininha"
            placeholder="Ex.: Rede, Cielo, Stone..."
            value={operadora}
            onChange={(e) => setOperadora(e.target.value)}
            disabled={transferindo}
          />
          <TextField
            size="small"
            type="date"
            label="Data"
            InputLabelProps={{ shrink: true }}
            value={data}
            onChange={(e) => setData(e.target.value)}
            disabled={transferindo}
            sx={{ minWidth: 150 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Valor pago no cartão"
            placeholder="0,00"
            value={exibir(valorBruto)}
            onChange={(e) => handleChangeValorBruto(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
            disabled={transferindo}
          />
          <TextField
            fullWidth
            size="small"
            label="Valor pago pela operadora"
            placeholder="0,00"
            value={exibir(valorLiquido)}
            onChange={(e) => handleChangeValorLiquido(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
            disabled={transferindo}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Taxa da operadora</Typography>
          <Typography variant="body1" fontWeight={700} color={taxa > 0 ? 'error.main' : 'text.primary'}>
            R$ {formatMoeda(taxa)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleCancel} disabled={transferindo}>Cancelar</Button>
        <Button variant="outlined" autoFocus disabled={transferindo} onClick={confirmar}>
          {transferindo ? 'Transferindo...' : 'Confirmar transferência'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
